/** Runs tasks one at a time, in the order they were queued. */
export class ExclusiveQueue {
    private tail: Promise<unknown> = Promise.resolve();

    /**
     * Runs `task` once every earlier task has finished. A caller cancelled before its turn stops
     * waiting straight away and never runs `task`; `whenCancelled` supplies its result instead.
     * Later callers still wait for the earlier tasks.
     */
    public run<T>(
        task: () => Promise<T>,
        whenCancelled: () => T,
        signal?: AbortSignal,
    ): Promise<T> {
        const earlier = this.tail;
        const result = waitForTurn(earlier, signal).then(() =>
            signal?.aborted ? whenCancelled() : task(),
        );
        this.tail = Promise.allSettled([earlier, result]);
        return result;
    }
}

function waitForTurn(earlier: Promise<unknown>, signal: AbortSignal | undefined): Promise<void> {
    return new Promise((resolve) => {
        const start = () => resolve();
        void earlier.then(start, start);
        if (signal?.aborted) {
            resolve();
        } else {
            signal?.addEventListener('abort', start, { once: true });
        }
    });
}
